import React, { useEffect, useState } from "react";
import { Table, Tag, DatePicker, Select, Space, Modal, Button, Descriptions } from "antd";
import { Eye } from "lucide-react";
import moment from "moment";
import { getAuditLogs, getAuditModules } from "../../../service/auditLogService";

const { RangePicker } = DatePicker;
const { Option } = Select;

const AuditLogs = () => {
  const [logs, setLogs]                       = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [selectedLog, setSelectedLog]         = useState(null);
  const [modalVisible, setModalVisible]       = useState(false);
  const [availableModules, setAvailableModules] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ module: "", action: "", status: "", startDate: "", endDate: "" });

  // ── Data fetching ───────────────────────────────────────────────────────────
  const fetchLogs = async (page = 1, pageSize = 20, currentFilters = filters) => {
    try {
      setLoading(true);
      const response = await getAuditLogs({ page, limit: pageSize, ...currentFilters });
      setLogs(response.data);
      setPagination(prev => ({ ...prev, current: page, total: response.total }));
    } catch (err) {
      console.error("Error fetching audit logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      const dynamicModules = await getAuditModules();
      const defaults = [
        "AUTH", "USER", "EMPLOYEE", "PRODUCT", "SALE", "PURCHASE",
        "CUSTOMER", "SUPPLIER", "EXPENSE", "STOCK", "LEAVE",
        "LEAVE_ALLOCATION", "HOLIDAY", "PAYROLL",
      ];
      const combined = Array.from(new Set([...defaults, ...(dynamicModules.data || [])]))
        .sort((a, b) => a.localeCompare(b));
      setAvailableModules(combined);
    } catch (err) {
      console.error("Error loading modules:", err);
    }
  };

  useEffect(() => { fetchLogs(); loadModules(); }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleTableChange = (pag) => fetchLogs(pag.current, pag.pageSize);

  const handleFilterChange = (value, name) => {
    const newFilters = { ...filters, [name]: value ?? "" };
    setFilters(newFilters);
    fetchLogs(1, pagination.pageSize, newFilters);
  };

  const handleDateChange = (dates) => {
    const newFilters = {
      ...filters,
      startDate: dates ? dates[0].format("YYYY-MM-DD") : "",
      endDate:   dates ? dates[1].format("YYYY-MM-DD") : "",
    };
    setFilters(newFilters);
    fetchLogs(1, pagination.pageSize, newFilters);
  };

  // ── Diff helper ─────────────────────────────────────────────────────────────
  const getDiffData = (oldData, newData) => {
    if (!oldData || !newData) return { oldDiff: oldData, newDiff: newData };
    const oldDiff = {}, newDiff = {};
    const SKIP = ["updatedAt", "createdAt", "__v", "_id"];
    const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
    allKeys.forEach((key) => {
      if (SKIP.includes(key)) return;
      const ov = oldData[key], nv = newData[key];
      if (JSON.stringify(ov) !== JSON.stringify(nv)) {
        if (ov !== undefined) oldDiff[key] = ov;
        if (nv !== undefined) newDiff[key] = nv;
      }
    });
    return { oldDiff, newDiff };
  };

  // ── Action colour map ────────────────────────────────────────────────────────
  const actionColor = (action) => {
    const map = { CREATE: "green", UPDATE: "orange", DELETE: "red", LOGIN: "cyan", APPROVE: "geekblue", REJECT: "volcano", GENERATE: "purple" };
    return map[action] || "default";
  };

  // ── Columns ──────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Date & Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("YYYY-MM-DD HH:mm:ss"),
      width: 170,
    },
    {
      title: "User",
      dataIndex: "userName",
      key: "userName",
      render: (text, record) => (
        <span>
          {record.userId?.name || text}
          <br />
          <small className="text-muted">{record.userId?.user_name || ""}</small>
        </span>
      ),
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text) => <Tag color={actionColor(text)}>{text}</Tag>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
      width: 140,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text, record) => (
        <Space>
          <Tag color={text === "SUCCESS" ? "success" : "error"}>{text}</Tag>
          <Button type="text" icon={<Eye size={16} />} onClick={() => { setSelectedLog(record); setModalVisible(true); }} />
        </Space>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Audit Logs</h4>
              <h6>Track all system activities and user actions</h6>
            </div>
          </div>
        </div>

        {/* Filters + Table */}
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <Space wrap>
                  <Select
                    placeholder="Filter by Module"
                    style={{ width: 200 }}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    onChange={(val) => handleFilterChange(val, "module")}
                  >
                    {availableModules.map((mod) => (
                      <Option key={mod} value={mod}>{mod}</Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Filter by Action"
                    style={{ width: 160 }}
                    allowClear
                    onChange={(val) => handleFilterChange(val, "action")}
                  >
                    {["CREATE", "UPDATE", "DELETE", "LOGIN", "APPROVE", "REJECT", "GENERATE"].map((a) => (
                      <Option key={a} value={a}>{a}</Option>
                    ))}
                  </Select>

                  <Select
                    placeholder="Status"
                    style={{ width: 130 }}
                    allowClear
                    onChange={(val) => handleFilterChange(val, "status")}
                  >
                    <Option value="SUCCESS">SUCCESS</Option>
                    <Option value="FAILED">FAILED</Option>
                  </Select>

                  <RangePicker onChange={handleDateChange} />
                </Space>
              </div>
            </div>

            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={logs}
                rowKey="_id"
                loading={loading}
                pagination={{
                  current:         pagination.current,
                  pageSize:        pagination.pageSize,
                  total:           pagination.total,
                  showSizeChanger: true,
                  showTotal:       (total) => `Total ${total} entries`,
                }}
                onChange={handleTableChange}
                scroll={{ x: 1000 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        title="Audit Log Details"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>Close</Button>,
        ]}
        width={820}
      >
        {selectedLog && (() => {
          const { oldDiff, newDiff } = getDiffData(selectedLog.oldData, selectedLog.newData);
          return (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Timestamp">
                {moment(selectedLog.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </Descriptions.Item>
              <Descriptions.Item label="User">
                {selectedLog.userId?.name || selectedLog.userName}
                {selectedLog.userId?.user_name ? ` (${selectedLog.userId.user_name})` : ""}
              </Descriptions.Item>
              <Descriptions.Item label="Module">
                <Tag color="blue">{selectedLog.module}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Action">
                <Tag color={actionColor(selectedLog.action)}>{selectedLog.action}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Description">{selectedLog.description}</Descriptions.Item>
              <Descriptions.Item label="IP Address">{selectedLog.ipAddress}</Descriptions.Item>
              <Descriptions.Item label="User Agent">{selectedLog.userAgent}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedLog.status === "SUCCESS" ? "success" : "error"}>{selectedLog.status}</Tag>
              </Descriptions.Item>
              {selectedLog.reason && (
                <Descriptions.Item label="Reason">{selectedLog.reason}</Descriptions.Item>
              )}
              <Descriptions.Item label="Before (changed fields)">
                <pre style={{ maxHeight: 200, overflow: "auto", background: "#fff5f5", padding: 8, borderRadius: 4 }}>
                  {oldDiff && Object.keys(oldDiff).length > 0
                    ? JSON.stringify(oldDiff, null, 2)
                    : "— No previous data —"}
                </pre>
              </Descriptions.Item>
              <Descriptions.Item label="After (changed fields)">
                <pre style={{ maxHeight: 200, overflow: "auto", background: "#f6ffed", padding: 8, borderRadius: 4 }}>
                  {newDiff && Object.keys(newDiff).length > 0
                    ? JSON.stringify(newDiff, null, 2)
                    : "— No new data —"}
                </pre>
              </Descriptions.Item>
            </Descriptions>
          );
        })()}
      </Modal>
    </div>
  );
};

export default AuditLogs;
